import { useEffect, useState } from 'react';

/**
 * Visibility for a transient, freshness-gated element (vote-result toast/banner).
 * `true` while `at + durationMs` is still in the future, then flips to `false`.
 *
 * Initial state is computed from `at` (not a blind `true`), so mounting on an already
 * expired timestamp never flashes a stale frame. Re-arms when `at` changes.
 */
export function useTransientVisible(at: number, durationMs: number): boolean {
    const [visible, setVisible] = useState(() => Date.now() - at < durationMs);

    useEffect(() => {
        const remaining = durationMs - (Date.now() - at);
        if (remaining <= 0) {
            setVisible(false);
            return undefined;
        }
        setVisible(true);
        const timer = window.setTimeout(() => setVisible(false), remaining);
        return () => window.clearTimeout(timer);
    }, [at, durationMs]);

    return visible;
}
